/**
 * Authenticated API client for the Keel platform backend.
 *
 * Usage:
 *   const api = useApiClient()
 *   const devices = await api.get('/api/v1/tenants/xxx/devices')
 */

import { useAuth } from 'react-oidc-context'
import { useCallback } from 'react'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API error ${status}: ${body}`)
    this.name = 'ApiError'
  }
}

async function request<T>(
  path: string,
  init: RequestInit,
  token: string | undefined,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const resp = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  })

  const text = await resp.text()

  if (!resp.ok) {
    throw new ApiError(resp.status, text)
  }

  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/** React hook that returns an authenticated API client bound to the current user's token. */
export function useApiClient() {
  const { user } = useAuth()
  const token = user?.access_token

  const get = useCallback(
    <T>(path: string) => request<T>(path, { method: 'GET' }, token),
    [token],
  )

  const post = useCallback(
    <T>(path: string, body: unknown) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }, token),
    [token],
  )

  const put = useCallback(
    <T>(path: string, body: unknown) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, token),
    [token],
  )

  const del = useCallback(
    (path: string) => request<void>(path, { method: 'DELETE' }, token),
    [token],
  )

  const patch = useCallback(
    <T>(path: string, body: unknown) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, token),
    [token],
  )

  const postForm = useCallback(
    <T>(path: string, body: FormData) => {
      // Do not set Content-Type header — browser sets it with the boundary for multipart
      const url = path.startsWith('http') ? path : path
      return fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      }).then(async (res) => {
        if (!res.ok) {
          const text = await res.text()
          throw new Error(`${res.status}: ${text}`)
        }
        return res.json() as Promise<T>
      })
    },
    [token],
  )

  return { get, post, put, del, patch, postForm }
}
